import { useTranslation } from 'react-i18next';
import { SVGAssets } from '@business/assets/vectors';
import { useState, useRef, useEffect, useMemo, JSX } from 'react';
import { useSelector } from 'react-redux';
import {
  getShareRefreshCounter,
  getAssetRefreshCounter
} from '@ports/common/sessionStateSlice';
import {
  useAssetsShareWithMeMutation,
  useGetAssetActivitiesCountQuery,
  useGetAssetLocationsQuery,
  useGetAssetThingsQuery,
  useGetAssetTypeListQuery
} from '@ports/internal-services/asset/assetSlice';
import { useRecipesCategoryListQuery } from '@ports/internal-services/recipe/recipeSlice';
import { useGetPeopleByNameQuery } from '@ports/internal-services/people/peopleSlice';
import { MultiSelectDropdown } from '../MultiSelectDropdown/MultiSelectDropdown';
import { Button } from '../Button';
import { DateRangePicker } from '../DateRangePicker/DateRangePicker';
import { Select } from '../Select';
interface FilterOption {
  value: string;
  label: string;
  parentCategoryId?: string;
}

interface FilterConfig {
  key: string;
  placeholder: string;
  options: FilterOption[];
}

interface FiltersProps {
  setFilterOptions: (options: any) => void;
  filterOptions?: any;
  selectedView?: 'grid' | 'list';
  onViewChange?: (view: 'grid' | 'list') => void;
  selectedContributorUserId?: string;
  currentUserId?: string;
  isExcludeFromFilter?: boolean;
  isFromRecipesPage?: boolean;
  isFromFavoritesPage?: boolean;
  module?: string;
  peopleId?: string;
  sortOptions?: { value: string; label: string }[];
  momentId?: string;
  isUpdateFilters?: boolean;
  setIsUpdateFilters?: (value: boolean) => void;
  isFromMemoriesPage?: boolean;
  folderId?: string;
  isFromAddMemory?: boolean;
  isFromFoldersPage?: boolean;
  tab?: 'myFiles' | 'sharedWithMe';
  isInsidePeople?: boolean;
  isSharedWithMe?: boolean;
  isMyFiles?: boolean;
  extraPayload?: any;
}

export const Filters = ({
  setFilterOptions,
  filterOptions,
  selectedView = 'grid',
  onViewChange,
  selectedContributorUserId,
  currentUserId,
  isExcludeFromFilter,
  isFromRecipesPage,
  isFromFavoritesPage,
  module,
  peopleId,
  sortOptions,
  momentId,
  isUpdateFilters,
  setIsUpdateFilters,
  isFromMemoriesPage,
  folderId,
  isFromAddMemory,
  isFromFoldersPage,
  tab,
  isInsidePeople,
  isSharedWithMe,
  isMyFiles,
  extraPayload
}: FiltersProps) => {
  const iconClass = 'w-4 h-4 ml-2 !text-[#505050]';
  const { t } = useTranslation();
  const [isViewDropdownOpen, setIsViewDropdownOpen] = useState<boolean>(false);
  const [isFavoritesActive, setIsFavoritesActive] = useState<boolean>(false);
  const [categoryId, setCategoryId] = useState<string>('');
  const [selectedMainCategories, setSelectedMainCategories] = useState<
    string[]
  >([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState<string[]>(
    []
  );
  const [allSubcategoriesCache, setAllSubcategoriesCache] = useState<
    FilterOption[]
  >([]);
  const [sharedWithOptions, setSharedWithOptions] = useState<FilterOption[]>(
    []
  );
  const [isInitialFetch, setIsInitialFetch] = useState(true);
  const [categoriesToFetch, setCategoriesToFetch] = useState<string[]>([]);
  const [isFilterClick, setIsFilterClick] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [SharedWithMe] = useAssetsShareWithMeMutation();
  const shareRefreshCounter = useSelector(getShareRefreshCounter);
  const assetRefreshCounter = useSelector(getAssetRefreshCounter);

  const handleFilterChange = (key: string, selectedValues: any) => {
    setFilterOptions((prev: any) => ({
      ...prev,
      [key]: selectedValues
    }));
  };

  const handleFavoritesToggle = () => {
    const newFavoritesState = !isFavoritesActive;
    setIsFavoritesActive(newFavoritesState);

    setFilterOptions((prev: any) => ({
      ...prev,
      favorites: newFavoritesState ? true : undefined
    }));
  };

  const handleViewSelect = (view: 'grid' | 'list') => {
    if (onViewChange) {
      onViewChange(view);
    }
    setIsViewDropdownOpen(false);
  };

  /* ✅ SYNC FAVORITES FROM REDUX */
  useEffect(() => {
    if (typeof filterOptions?.favorites === 'boolean') {
      setIsFavoritesActive(filterOptions.favorites);
    }
  }, [filterOptions?.favorites]);

  /* ✅ SYNC MAIN CATEGORIES FROM REDUX */
  useEffect(() => {
    if (Array.isArray(filterOptions?.categoryId)) {
      setSelectedMainCategories(filterOptions.categoryId);
    } else if (!filterOptions?.categoryId) {
      setSelectedMainCategories([]);
    }
  }, [filterOptions?.categoryId]);

  /* ✅ SYNC SUBCATEGORIES FROM REDUX */
  useEffect(() => {
    if (Array.isArray(filterOptions?.subcategoryId)) {
      setSelectedSubCategories(filterOptions.subcategoryId);
    } else if (!filterOptions?.subcategoryId) {
      setSelectedSubCategories([]);
    }
  }, [filterOptions?.subcategoryId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsViewDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const accessType =
    filterOptions?.accessType === 'private' ? 'private' : 'public';

  const { data: thingsData, refetch: refetchThings } = useGetAssetThingsQuery(
    {
      module: momentId ? 'moments' : module,
      peopleId,
      momentId,
      userFolderId: isFromFoldersPage === true ? 'all' : folderId || undefined,
      sortby: 'tagName',
      orderby: 'ASC',
      isInsidePeople,
      isSharedWithMe,
      isMyFiles,
      access: accessType,
      ...(extraPayload || {})
    },
    { refetchOnMountOrArgChange: true }
  );

  const { data: peopleData, refetch: refetchPeople } = useGetPeopleByNameQuery(
    {
      module: momentId ? 'moments' : module,
      peopleId,
      momentId,
      userFolderId: isFromFoldersPage === true ? 'all' : folderId || undefined,
      sortby: 'name',
      orderby: 'ASC',
      isInsidePeople,
      isSharedWithMe,
      isMyFiles,
      access: accessType,
      ...(extraPayload || {})
    },
    { refetchOnMountOrArgChange: true }
  );
  const { data: locationData, refetch: refetchLocations } =
    useGetAssetLocationsQuery(
      {
        module: momentId ? 'moments' : module,
        peopleId,
        momentId,
        userFolderId:
          isFromFoldersPage === true ? 'all' : folderId || undefined,
        sortby: 'name',
        orderby: 'ASC',
        isInsidePeople,
        isSharedWithMe,
        isMyFiles,
        access: accessType,
        ...(extraPayload || {})
      },
      { refetchOnMountOrArgChange: true }
    );

  const { data: recipesCategoryListData, refetch: refetchRecipesCategoryList } =
    useRecipesCategoryListQuery({
      userFolderId: isFromFoldersPage === true ? 'all' : folderId || undefined,
      access: accessType,
      peopleId: peopleId || undefined,
      ...(extraPayload || {})
    });

  const {
    data: recipesSubCategoryListData,
    refetch: refetchRecipesSubCategoryList
  } = useRecipesCategoryListQuery(
    {
      categoryId: categoryId,
      userFolderId: isFromFoldersPage === true ? 'all' : folderId || undefined,
      ...(isFilterClick ? { filter: true } : {}),
      access: accessType,
      peopleId: peopleId || undefined,
      ...(extraPayload || {})
    },
    { skip: !categoryId }
  );

  const { data: assetTypes, refetch: refetchAssetTypes } =
    useGetAssetTypeListQuery(
      {
        module: momentId ? 'moments' : module,
        momentId,
        peopleId,
        userFolderId:
          isFromFoldersPage === true ? 'all' : folderId || undefined,
        isInsidePeople,
        isSharedWithMe,
        isMyFiles,
        access: accessType,
        ...(extraPayload || {})
      },
      { refetchOnMountOrArgChange: true }
    );

  const { data: interactionData, refetch: refetchInteractionData } =
    useGetAssetActivitiesCountQuery(
      {
        module: momentId ? 'moments' : module,
        momentId,
        userFolderId:
          isFromFoldersPage === true ? 'all' : folderId || undefined,
        peopleId,
        isInsidePeople,
        isSharedWithMe,
        isMyFiles,
        access: accessType,
        ...(extraPayload || {})
      },
      { refetchOnMountOrArgChange: true }
    );

  useEffect(() => {
    const getSharedWithData = async () => {
      try {
        const response = await SharedWithMe({
          assetsSharedWithMe: {
            module: momentId ? 'moments' : module,
            peopleId: peopleId || undefined,
            isInsidePeople: isInsidePeople || undefined,
            isSharedWithMe: isSharedWithMe || undefined,
            isMyFiles: isMyFiles || undefined,
            access:
              filterOptions?.accessType === 'private' ? 'private' : 'public',
            userFolderId:
              isFromFoldersPage === true ? 'all' : folderId || undefined,
            ...(extraPayload || {})
          }
        }).unwrap();
        const options =
          response?.data?.rows?.map((item: any) => ({
            value: item?.peopleId,
            label: item?.name,
            count: Number(
              item?.sharedCount ?? item?.groupCount ?? item?.favoritesCount ?? 0
            )
          })) || [];
        setSharedWithOptions(options);
      } catch {
        setSharedWithOptions([]);
      }
    };
    if (module || peopleId || momentId) {
      getSharedWithData();
    }
  }, [
    module,
    peopleId,
    momentId,
    folderId,
    SharedWithMe,
    shareRefreshCounter,
    assetRefreshCounter,
    filterOptions?.accessType
  ]);

  useEffect(() => {
    if (assetRefreshCounter === 0) return;
    refetchThings();
    refetchPeople();
    refetchLocations();
    refetchAssetTypes();
    refetchInteractionData();
  }, [assetRefreshCounter]);

  const mediaTypeOptions = useMemo(() => {
    return (
      assetTypes?.data
        ?.filter((item: any) => {
          if (item?.key === 'all') return false; // always exclude 'all'

          if (
            isFromAddMemory &&
            (item?.key === 'document' || item?.key === 'audio')
          ) {
            return false;
          }

          return true;
        })
        ?.map((item: any) => ({
          value: item?.key,
          label: item?.label,
          count: parseInt(item?.count) || 0
        })) || []
    );
  }, [assetTypes, isFromAddMemory]);

  const isRestrictedMyFiles =
    module === 'myFiles' &&
    selectedContributorUserId &&
    currentUserId &&
    selectedContributorUserId !== currentUserId;

  // ✅ Handle main category selection
  const handleMainCategorySelect = (categoryIds: string[]) => {
    setSelectedMainCategories(categoryIds);
    handleFilterChange('categoryId', categoryIds);

    // Filter only main categories (exclude subcategories)
    const mainCategoryIds = categoryIds.filter(id =>
      recipesCategoryOptions.some(opt => opt.value === id)
    );

    if (mainCategoryIds.length > 0) {
      setCategoryId(mainCategoryIds[mainCategoryIds.length - 1]); // Use last selected main category
    } else {
      setCategoryId('');
    }
  };

  // ✅ Handle location selection ('no_location' is exclusive of other locations)
  const handleLocationSelect = (selectedValues: string[]) => {
    if (selectedValues.includes('no_location')) {
      handleFilterChange(
        'locations',
        isFromMemoriesPage ? [] : ['no_location']
      );
    } else {
      handleFilterChange('locations', selectedValues);
    }
  };

  // ✅ Handle subcategory selection
  const handleSubCategorySelect = (subcategoryIds: string[]) => {
    setSelectedSubCategories(subcategoryIds);
    handleFilterChange('subcategoryId', subcategoryIds);
  };

  // ✅ Add handler for when user clicks a main category
  const handleMainCategoryClick = (categoryId: string) => {
    setIsFilterClick(true);
    setCategoryId(categoryId);
  };

  useEffect(() => {
    if (categoryId) {
      refetchRecipesSubCategoryList();
    }
  }, [categoryId, refetchRecipesSubCategoryList]);

  useEffect(() => {
    refetchThings();
    refetchPeople();
    refetchLocations();
    refetchRecipesCategoryList();
    refetchAssetTypes();
    refetchInteractionData();
  }, [folderId]);

  function refetchAll() {
    if (!isUpdateFilters) return;

    refetchThings();
    refetchPeople();
    refetchLocations();
    refetchRecipesCategoryList();
    refetchAssetTypes();

    setIsUpdateFilters(false);
  }

  /* ✅ PRE-FETCH SUBCATEGORY LABELS FOR EXISTING SELECTIONS */
  useEffect(() => {
    // If we have both category and subcategory IDs on mount, queue them for fetching
    if (
      filterOptions?.categoryId &&
      Array.isArray(filterOptions.categoryId) &&
      filterOptions.categoryId.length > 0 &&
      filterOptions?.subcategoryId &&
      Array.isArray(filterOptions.subcategoryId) &&
      filterOptions.subcategoryId.length > 0 &&
      isInitialFetch
    ) {
      setCategoriesToFetch(filterOptions.categoryId);
      setIsInitialFetch(false);
    }
  }, []);

  /* ✅ PROCESS CATEGORY FETCH QUEUE */
  useEffect(() => {
    if (categoriesToFetch.length > 0 && !categoryId) {
      // Fetch the first category in the queue
      const [nextCategory, ...remaining] = categoriesToFetch;
      setCategoryId(nextCategory);
      setCategoriesToFetch(remaining);
    }
  }, [categoriesToFetch, categoryId]);

  /* ✅ AFTER SUBCATEGORY DATA IS FETCHED, MOVE TO NEXT CATEGORY */
  useEffect(() => {
    if (
      recipesSubCategoryListData &&
      categoryId &&
      categoriesToFetch.length > 0
    ) {
      // Clear categoryId to allow fetching next category
      setCategoryId('');
      setIsFilterClick(false);
    }
  }, [recipesSubCategoryListData, categoryId, categoriesToFetch.length]);

  const thingsOptions = useMemo(() => {
    return (
      thingsData?.data?.map(item => ({
        value: item?.tagName,
        label: item?.tagName,
        count: item?.asset_count || item?.tag_count || 0
      })) || []
    );
  }, [thingsData]);

  const peopleOptions = useMemo(() => {
    return (
      peopleData?.data?.rows
        ?.map(item => ({
          value: item?.id,
          label: item?.name,
          profilePicUrl: item?.profileImage,
          count: item?.asset_count || item?.moment_count || 0
        }))
        ?.sort((a, b) =>
          (a.label || '').localeCompare(b.label || '', undefined, {
            sensitivity: 'base'
          })
        ) || []
    );
  }, [peopleData]);

  const locationOptions = useMemo(() => {
    // ✅ Backend may represent "no location" as empty/null OR as the literal
    // string "No Location" — treat both as the same synthetic bucket.
    const isNoLocationValue = (location: string) => {
      const trimmed = location?.trim?.() || '';
      return !trimmed || trimmed.toLowerCase() === 'no location';
    };

    const noLocationCount =
      locationData?.data?.reduce((count: number, item: any) => {
        return isNoLocationValue(item?.location)
          ? count + Number(item?.location_count || 0)
          : count;
      }, 0) || 0;

    const locations =
      locationData?.data
        ?.filter((item: any) => !isNoLocationValue(item?.location))
        .map((item: any) => ({
          value: item.location,
          label: item.location,
          count: item.location_count || 0
        })) || [];

    return [
      ...(noLocationCount > 0
        ? [
            {
              value: 'no_location',
              label: t('photosPage.noLocation'),
              count: noLocationCount
            }
          ]
        : []),
      ...locations
    ];
  }, [locationData]);

  const recipesCategoryOptions = useMemo(() => {
    return (
      recipesCategoryListData?.data?.map(item => ({
        value: item?.id,
        label: item?.name,
        recipeCount: item?.recipeCount || 0
      })) || []
    );
  }, [recipesCategoryListData]);

  const recipesSubCategoryOptions = useMemo(() => {
    const newSubcategories =
      recipesSubCategoryListData?.data?.map(item => ({
        value: item?.id,
        label: item?.name,
        parentCategoryId: categoryId, // ✅ Store parent category ID
        recipeCount: item?.recipeCount || 0
      })) || [];

    // ✅ Update cache with new subcategories
    if (newSubcategories.length > 0) {
      setAllSubcategoriesCache(prev => {
        const existingIds = new Set(prev.map(sub => sub.value));
        const toAdd = newSubcategories.filter(
          sub => !existingIds.has(sub.value)
        );
        return [...prev, ...toAdd];
      });
    }

    return newSubcategories;
  }, [recipesSubCategoryListData, categoryId]);

  const interactionOptions = useMemo(() => {
    const iconMap: Record<string, JSX.Element> = {
      likes: <SVGAssets.LikeIcons className={iconClass} />,
      comments: <SVGAssets.CommentIcons className={iconClass} />,
      diary: <SVGAssets.DiaryIcon className={iconClass} />
    };

    return (
      interactionData?.data?.map((item: any) => ({
        value: item?.key,
        label: item?.label,
        count: parseInt(item?.count) || 0,
        startDecorator: iconMap[item?.key]
      })) || []
    );
  }, [interactionData]);

  const isSharedWithMeTab = tab === 'sharedWithMe';
  const isSharingContext =
    module === 'myFiles' || module === 'sharedFiles' || isSharedWithMeTab;
  const showOnlyCoreSharingFilters =
    isSharingContext &&
    (module === 'sharedFiles' ||
      selectedContributorUserId !== currentUserId ||
      isSharedWithMeTab);
  const shouldHideInteractions = isSharingContext || isExcludeFromFilter;

  const filterConfigs: FilterConfig[] = [
    {
      key: 'mediaType',
      placeholder: t('photosPage.mediaType'),
      options: mediaTypeOptions
    },
    {
      key: 'things',
      placeholder: t('photosPage.things'),
      options: thingsOptions
    },
    {
      key: 'people',
      placeholder: t('photosPage.people'),
      options: peopleOptions
    },
    {
      key: 'locations',
      placeholder: t('photosPage.location'),
      options: locationOptions
    },
    {
      key: 'foodType',
      placeholder: t('recipesTab.foodType'),
      options: recipesCategoryOptions
    },
    {
      key: 'sharedWith',
      placeholder: t('photosPage.sharedWith'),
      options: sharedWithOptions
    }
  ];

  const visibleFilterConfigs = filterConfigs.filter(filter => {
    if (showOnlyCoreSharingFilters) {
      return ['mediaType', 'things', 'locations'].includes(filter.key);
    }

    if (isFromRecipesPage) {
      return filter.key !== 'locations' && filter.key !== 'things';
    }
    if (filter.key === 'foodType' && !isFromRecipesPage) {
      return false;
    }

    // if (
    //   filter.key === 'sharedWith' &&
    //   (module === 'myFiles' || module === 'sharedFiles') &&
    //   isExcludeFromFilter
    // ) {
    //   return false;
    // }
    return true;
  });

  const otherFilterConfigs = visibleFilterConfigs.filter(
    filter => filter.key !== 'mediaType'
  );

  const recentOptions = useMemo(
    () => [
      ...(sortOptions || []),
      { value: 'mostRecent', label: t('photosPage.mostRecent') },
      { value: 'leastRecent', label: t('photosPage.leastRecent') }
    ],
    [sortOptions]
  );

  const handleRemoveChip = (key: string, value: string) => {
    // ✅ If removing a main category, also remove its subcategories
    if (key === 'categoryId') {
      // Find subcategories that belong to this category
      const subcategoriesToRemove = allSubcategoriesCache
        .filter(sub => sub.parentCategoryId === value)
        .map(sub => sub.value);

      // Remove subcategories from state
      setSelectedSubCategories(prev => {
        const filtered = prev.filter(
          subId => !subcategoriesToRemove.includes(subId)
        );
        return filtered;
      });

      // Update main categories
      setSelectedMainCategories(prev => prev.filter(id => id !== value));

      // Update filter options for both category and subcategory
      setFilterOptions((prev: any) => {
        const updatedSubcategories = (prev.subcategoryId || []).filter(
          (subId: string) => !subcategoriesToRemove.includes(subId)
        );

        return {
          ...prev,
          categoryId: prev.categoryId?.filter((item: string) => item !== value),
          subcategoryId:
            updatedSubcategories.length > 0 ? updatedSubcategories : undefined
        };
      });
    } else {
      // For other filters, just remove the item
      setFilterOptions((prev: any) => ({
        ...prev,
        [key]: prev[key]?.filter((item: string) => item !== value)
      }));

      // If removing a subcategory, update the state
      if (key === 'subcategoryId') {
        setSelectedSubCategories(prev => prev.filter(id => id !== value));
      }
    }
  };

  // ✅ Handle removing individual months or years from date filter
  const handleRemoveDateChip = (type: 'month' | 'year', value: number) => {
    setFilterOptions((prev: any) => {
      const currentDate = prev.date || { months: [], years: [] };
      const updatedMonths =
        type === 'month'
          ? currentDate.months.filter((m: number) => m !== value)
          : currentDate.months;
      const updatedYears =
        type === 'year'
          ? currentDate.years.filter((y: number) => y !== value)
          : currentDate.years;

      // If both months and years are empty, remove the date key entirely
      if (updatedMonths.length === 0 && updatedYears.length === 0) {
        const newState = { ...prev };
        delete newState.date;
        return newState;
      }

      return {
        ...prev,
        date: { months: updatedMonths, years: updatedYears }
      };
    });
  };

  const getLabel = (options: FilterOption[], value: string) => {
    const found = options.find(opt => opt.value === value);
    return found ? found.label : value;
  };

  // ✅ Get month label from month number
  const getMonthLabel = (monthNum: number) => {
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ];
    return monthNames[monthNum - 1] || monthNum.toString();
  };

  return (
    <>
      <div
        className={`flex flex-col md:flex-row ${
          isExcludeFromFilter || isFromRecipesPage
            ? 'w-full md:justify-between'
            : 'flex-wrap gap-1'
        } md:items-center`}>
        {!isFromFavoritesPage && (
          <>
            {!isFromFavoritesPage &&
              !isFromRecipesPage &&
              !isExcludeFromFilter &&
              !isRestrictedMyFiles && (
                <Button
                  btnStyle="link"
                  onClick={handleFavoritesToggle}
                  className={`flex items-center py-1.5 rounded-md transition-colors`}>
                  {isFavoritesActive ? (
                    <SVGAssets.YellowFav className="w-5 h-5 mr-2" />
                  ) : (
                    <SVGAssets.FavoritesIcon className={`w-5 h-5 mr-2 `} />
                  )}
                  <span
                    className={`text-sm font-semibold text-[#505050] cursor-pointer`}>
                    {t('favorites')}
                  </span>
                </Button>
              )}
          </>
        )}

        <div
          className={`flex flex-col md:flex-row ${
            isExcludeFromFilter || isFromRecipesPage
              ? 'w-full md:justify-between md:items-center'
              : 'flex-wrap gap-1'
          }`}>
          <div className="flex flex-wrap gap-1 items-center">
            {!isExcludeFromFilter && isFromRecipesPage && (
              <Button
                btnStyle="link"
                onClick={handleFavoritesToggle}
                className={`flex items-center py-1.5 rounded-md transition-colors`}>
                {isFavoritesActive ? (
                  <SVGAssets.YellowFav className="w-5 h-5 mr-2" />
                ) : (
                  <SVGAssets.FavoritesIcon className={`w-5 h-5 mr-2 `} />
                )}
                <span
                  className={`text-sm font-semibold text-[#505050] cursor-pointer`}>
                  {t('favorites')}
                </span>
              </Button>
            )}
            {/* ✅ MEDIA TYPE */}
            <MultiSelectDropdown
              key="mediaType"
              name="mediaType"
              options={filterConfigs[0].options}
              placeholder={filterConfigs[0].placeholder}
              value={filterOptions?.mediaType || []}
              onChange={selectedValues =>
                handleFilterChange('mediaType', selectedValues)
              }
              searchable
              searchPlaceholder="Search"
              showCount
            />

            {/* ✅ DATE RANGE */}
            <DateRangePicker
              placeholder={t('photosPage.date')}
              value={filterOptions?.date || { months: [], years: [] }}
              onChange={selectedValues =>
                handleFilterChange('date', selectedValues)
              }
            />

            {/* ✅ OTHER FILTERS */}
            {otherFilterConfigs.map(filter => (
              <MultiSelectDropdown
                key={filter.key}
                name={filter.key}
                options={filter.options}
                placeholder={filter.placeholder}
                value={
                  filter.key === 'foodType'
                    ? filterOptions?.categoryId || []
                    : filterOptions?.[filter.key] || []
                }
                onOpen={
                  [
                    'things',
                    'people',
                    'locations',
                    'foodType',
                    'mediaType'
                  ].includes(filter.key)
                    ? refetchAll
                    : undefined
                }
                onChange={selectedValues => {
                  if (filter.key === 'foodType') {
                    handleMainCategorySelect(selectedValues);
                  } else if (filter.key === 'locations') {
                    handleLocationSelect(selectedValues);
                  } else {
                    handleFilterChange(filter.key, selectedValues);
                  }
                }}
                searchable
                searchPlaceholder="Search"
                showCount={[
                  'things',
                  'people',
                  'locations',
                  'sharedWith'
                ].includes(filter.key)}
                {...(filter.key === 'foodType' && {
                  isHaveSubCategory: true,
                  onMainCategorySelect: handleMainCategoryClick,
                  subcategoryOptions: recipesSubCategoryOptions,
                  selectedMainCategories: selectedMainCategories,
                  onSubCategoryChange: handleSubCategorySelect,
                  selectedSubCategories: selectedSubCategories
                })}
              />
            ))}

            {/* ✅ INTERACTIONS */}
            {!shouldHideInteractions && !isFromMemoriesPage && (
              <MultiSelectDropdown
                name="interactions"
                options={interactionOptions}
                placeholder={t('photosPage.interactions')}
                value={filterOptions?.interactions || []}
                onChange={selectedValues =>
                  handleFilterChange('interactions', selectedValues)
                }
                searchable
                showCount
              />
            )}
          </div>

          <div className="flex items-center gap-1 mt-2 justify-end md:mt-0">
            {/* View Mode Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <Button
                btnStyle="link"
                onClick={() => setIsViewDropdownOpen(!isViewDropdownOpen)}
                className="px-1 py-1.5 text-sm rounded-md bg-white hover:bg-gray-50 transition-colors flex items-center gap-2">
                {selectedView === 'grid' ? (
                  <SVGAssets.GridIcon className="w-4 h-4" />
                ) : (
                  <SVGAssets.ListIcon className="w-4 h-4" />
                )}
                <SVGAssets.PricingArrow
                  className={`w-3 h-3 transition-transform ${
                    isViewDropdownOpen ? 'rotate-90' : '-rotate-90'
                  }`}
                />
              </Button>

              {isViewDropdownOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                  <Button
                    btnStyle="link"
                    onClick={() => handleViewSelect('grid')}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${
                      selectedView === 'grid' ? 'bg-gray-200 font-medium' : ''
                    }`}>
                    <SVGAssets.GridIcon className="w-4 h-4" />
                    <span>{t('gridView')}</span>
                  </Button>
                  <Button
                    btnStyle="link"
                    onClick={() => handleViewSelect('list')}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${
                      selectedView === 'list' ? 'bg-gray-100 font-medium' : ''
                    }`}>
                    <SVGAssets.ListIcon className="w-4 h-4" />
                    <span>{t('listView')}</span>
                  </Button>
                </div>
              )}
            </div>

            <Select
              name="sortBy"
              options={recentOptions}
              value={filterOptions?.sortBy || 'mostRecent'}
              onChange={e => handleFilterChange('sortBy', e.target.value)}
              labelClassName="!text-[#49712B] !font-semibold"
              selectClassName="text-sm"
              optionClassName="text-sm !text-[#49712B] !font-semibold"
            />
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4 mt-2">
        {Object.entries(filterOptions || {}).map(([key, values]: any) => {
          // ✅ Handle date filter separately (months and years)
          if (key === 'date' && values && typeof values === 'object') {
            const dateChips: JSX.Element[] = [];

            // Render month chips
            if (Array.isArray(values.months)) {
              values.months.forEach((month: number) => {
                dateChips.push(
                  <div
                    key={`month-${month}`}
                    className="flex items-center text-sm px-3 py-1 rounded-full bg-[#49712B] text-white">
                    <span className="mr-2 text-sm">{getMonthLabel(month)}</span>
                    <button
                      onClick={() => handleRemoveDateChip('month', month)}
                      className="ml-1">
                      <SVGAssets.CloseBtn className="w-3 h-3" />
                    </button>
                  </div>
                );
              });
            }

            // Render year chips
            if (Array.isArray(values.years)) {
              values.years.forEach((year: number) => {
                dateChips.push(
                  <div
                    key={`year-${year}`}
                    className="flex items-center text-sm px-3 py-1 rounded-full bg-[#49712B] text-white">
                    <span className="mr-2 text-sm">{year}</span>
                    <button
                      onClick={() => handleRemoveDateChip('year', year)}
                      className="ml-1">
                      <SVGAssets.CloseBtn className="w-3 h-3" />
                    </button>
                  </div>
                );
              });
            }

            return dateChips;
          }

          if (!Array.isArray(values)) return null;

          // ✅ Map categoryId and subcategoryId to their respective options
          let options: FilterOption[] = [];
          if (key === 'categoryId') {
            options = recipesCategoryOptions;
          } else if (key === 'subcategoryId') {
            options = allSubcategoriesCache;
          } else {
            options = filterConfigs.find(f => f?.key === key)?.options || [];
          }

          return values.map((value: string) => {
            const isSubcategory = key === 'subcategoryId';
            return (
              <div
                key={`${key}-${value}`}
                className={`flex items-center text-sm px-3 py-1 rounded-full ${
                  isSubcategory
                    ? 'bg-[#CDDD9A] text-[#49712B]'
                    : 'bg-[#49712B] text-white'
                }`}>
                <span className="mr-2 text-sm">{getLabel(options, value)}</span>

                <button
                  onClick={() => handleRemoveChip(key, value)}
                  className="ml-1">
                  <SVGAssets.CloseBtn className="w-3 h-3" />
                </button>
              </div>
            );
          });
        })}
      </div>
    </>
  );
};
